import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTriggerButton,
  Icon,
  Paragraph,
  Text,
  Tooltip,
  TooltipContent,
} from '@customerio/pluma-components/react';
import { ConnectionState, connectionButtonLabel } from '../connection-status';

const NO_CREDENTIAL_TOOLTIP = 'Select your credentials';
const NO_PERMISSION_TOOLTIP =
  "You don't have permission to set up this integration. If you'd like access, let an admin know.";

type SegmentOptionsMenuProps = {
  state: ConnectionState;
  isDisabled: boolean;
  canDelete: boolean;
  onDisable?: () => void;
  onDelete?: () => void;
};

/**
 * Disable and delete. The menu is hidden entirely outside the states that have a
 * destination to act on, and Delete stays visible but disabled without the delete
 * permission, so a reader can see the action exists.
 */
export function SegmentOptionsMenu({ state, isDisabled, canDelete, onDisable, onDelete }: SegmentOptionsMenuProps) {
  return (
    <Tooltip isDisabled={!isDisabled}>
      <DropdownMenu placement="bottom-end" isDisabled={isDisabled}>
        {/* Also on the trigger: the menu's own `isDisabled` does not reach the DOM, so
            without this the button stays focusable and clickable while the menu is off. */}
        <DropdownMenuTriggerButton
          variant="secondary"
          mr="200"
          isDisabled={isDisabled}
          data-testid="segment-in-actions-dropdown-trigger"
        >
          Options
        </DropdownMenuTriggerButton>
        <DropdownMenuContent>
          {state === ConnectionState.Disabled ? null : (
            <DropdownMenuItem data-test-segment-action="disable" isCritical onClick={onDisable}>
              Disable Connection
            </DropdownMenuItem>
          )}
          <DropdownMenuItem data-test-segment-action="delete" isCritical isDisabled={!canDelete} onClick={onDelete}>
            Delete Connection
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <TooltipContent>
        <Paragraph color="inherit" m="0">
          {NO_CREDENTIAL_TOOLTIP}
        </Paragraph>
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * The Options menu and the main control.
 *
 * A connected destination has no button: there is nothing to do to it from here
 * except through Options, so the state is reported rather than offered as an
 * action.
 *
 * The three permissions guard different controls, so a reader with `edit` but not
 * `delete` still opens the menu and still sees Delete, disabled.
 */
type SegmentMainControlProps = {
  state: ConnectionState;
  hasCredential: boolean;
  /** `journeys/segment_integration/create`. */
  canIntegrate: boolean;
  isBusy: boolean;
  onConnect?: () => void;
};

/**
 * The main control.
 *
 * A connected destination has no button: there is nothing left to do to it from
 * here except through Options, so the state is reported rather than offered. The
 * disabled tooltip names whichever reason applies, since "select a credential" and
 * "you lack permission" need different responses from the reader.
 */
export function SegmentMainControl({ state, hasCredential, canIntegrate, isBusy, onConnect }: SegmentMainControlProps) {
  const label = connectionButtonLabel(state);

  if (state === ConnectionState.Enabled) {
    return (
      <>
        <Icon name="check-circle" color="success" mr="100" />
        <Text weight="medium" color="subtle" verticalAlign="middle" data-test-configure-segment-btn>
          {label}
        </Text>
      </>
    );
  }

  return (
    <Tooltip isCustom isDisabled={hasCredential && canIntegrate}>
      {(context) => (
        <>
          <Button
            ref={context.reference.setReference}
            {...context.reference.getReferenceProps()}
            data-test-configure-segment-btn
            isDisabled={!hasCredential || isBusy || !canIntegrate}
            isLoading={isBusy}
            variant="primary"
            onClick={onConnect}
          >
            {label}
          </Button>
          <TooltipContent>
            <Paragraph color="inherit" m="0">
              {hasCredential ? NO_PERMISSION_TOOLTIP : NO_CREDENTIAL_TOOLTIP}
            </Paragraph>
          </TooltipContent>
        </>
      )}
    </Tooltip>
  );
}
